import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, Heart, Compass } from 'lucide-react';
import heroData from '../content/hero.json';

const HeroSection = () => {
  const { title, subtitle, description, tags, ctaButton, secondaryButton, stats } = heroData;
  
  // CMS format: tags is array of objects with 'tag' property
  const tagsArray = tags ? tags.map(tag => tag.tag || tag) : [];
  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-20 right-10 w-64 h-64 rounded-full bg-nitzan-mint opacity-10 blur-3xl"
          animate={{
            y: [0, 30, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute bottom-20 left-10 w-96 h-96 rounded-full bg-nitzan-sage opacity-10 blur-3xl"
          animate={{
            y: [0, -40, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </div>

      <div className="container-width relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Text Content */}
          <motion.div
            className="text-center lg:text-right space-y-6"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            {tagsArray.length > 0 && (
              <motion.div
                className="inline-flex items-center gap-2 bg-gradient-to-r from-nitzan-mint/20 to-nitzan-teal/20 px-6 py-3 rounded-full"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <Sparkles className="text-nitzan-teal" size={20} />
                <span className="text-nitzan-teal font-medium">{tagsArray[0]}</span>
              </motion.div>
            )}

            <motion.h1
              className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <span className="gradient-text">{title}</span>
            </motion.h1>

            <motion.p
              className="text-xl md:text-2xl text-nitzan-terracotta font-medium"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
            >
              {subtitle}
            </motion.p>

            <motion.p
              className="text-lg md:text-xl text-gray-700 leading-relaxed max-w-2xl mx-auto lg:mx-0"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9 }}
            >
              {description}
            </motion.p>

            <motion.div
              className="flex flex-wrap gap-4 justify-center lg:justify-start pt-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1 }}
            >
              <Link to="/contact">
                <motion.div
                  className="bg-gradient-to-r from-nitzan-teal to-nitzan-mint text-white px-8 py-4 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Heart size={20} />
                  <span>{ctaButton}</span>
                </motion.div>
              </Link>
              <motion.div
                className="bg-white text-nitzan-teal px-8 py-4 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-nitzan-teal flex items-center gap-2 cursor-pointer"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => window.scrollTo({ top: document.getElementById('about').offsetTop, behavior: 'smooth' })}
              >
                <Compass size={20} />
                <span>{secondaryButton}</span>
              </motion.div>
            </motion.div>

            {/* Stats */}
            {stats && stats.length > 0 && (
              <motion.div
                className="grid grid-cols-3 gap-6 pt-8 max-w-xl mx-auto lg:mx-0"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.3 }}
              >
                {stats.map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="text-3xl font-bold text-nitzan-teal">{stat.number}</div>
                    <div className="text-sm text-gray-600 mt-1">{stat.label}</div>
                  </div>
                ))}
              </motion.div>
            )}
          </motion.div>

          {/* Image/Placeholder */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <div className="relative aspect-square max-w-lg mx-auto">
              {/* Decorative circles */}
              <motion.div
                className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-gradient-to-br from-nitzan-sage to-nitzan-terracotta opacity-20"
                animate={{
                  y: [0, -20, 0],
                  rotate: [0, 180, 360],
                }}
                transition={{
                  duration: 10,
                  repeat: Infinity,
                  ease: "linear"
                }}
              />
              <motion.div
                className="absolute -bottom-6 -left-6 w-32 h-32 rounded-full bg-gradient-to-br from-nitzan-mint to-nitzan-teal opacity-20"
                animate={{
                  y: [0, 20, 0],
                  rotate: [360, 180, 0],
                }}
                transition={{
                  duration: 12,
                  repeat: Infinity,
                  ease: "linear"
                }}
              />

              {/* Main image placeholder */}
              <div className="relative z-10 w-full h-full rounded-3xl bg-gradient-to-br from-nitzan-sage via-nitzan-mint to-nitzan-teal shadow-2xl overflow-hidden">
                <div className="absolute inset-0 flex items-center justify-center bg-white/10 backdrop-blur-sm">
                  <div className="text-center text-white p-8">
                    <Sparkles size={64} className="mx-auto mb-4 opacity-60" />
                    <p className="text-2xl font-semibold">תמונת פרופיל</p>
                    <p className="text-sm mt-2 opacity-80">(להוספה בהמשך)</p>
                  </div>
                </div>
              </div>

              {/* Floating elements */}
              <motion.div
                className="absolute top-1/4 -right-8 bg-white p-4 rounded-2xl shadow-xl z-20"
                animate={{
                  y: [0, -15, 0],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-nitzan-mint to-nitzan-teal flex items-center justify-center">
                    <Heart className="text-white" size={24} />
                  </div>
                  <div>
                    <div className="font-semibold text-nitzan-teal">ריפוי עמוק</div>
                    <div className="text-xs text-gray-600">גוף ונפש</div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                className="absolute bottom-1/4 -left-8 bg-white p-4 rounded-2xl shadow-xl z-20"
                animate={{
                  y: [0, 15, 0],
                }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-nitzan-sage to-nitzan-terracotta flex items-center justify-center">
                    <Compass className="text-white" size={24} />
                  </div>
                  <div>
                    <div className="font-semibold text-nitzan-teal">מסלול אישי</div>
                    <div className="text-xs text-gray-600">התאמה מלאה</div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        animate={{
          y: [0, 10, 0],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      >
        <div className="w-6 h-10 border-2 border-nitzan-teal rounded-full flex items-start justify-center p-2">
          <motion.div
            className="w-1 h-2 bg-nitzan-teal rounded-full"
            animate={{
              y: [0, 12, 0],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;

